import React, { useState, useEffect } from 'react';
import { Modal, Btn, Input } from '../UI';
import * as api from '../../api';

// Two-step "Select From Previous Purchases" browser: pick a department, then
// pick lines from its purchasing history. Shared by every non-HR requisition
// form so the browsing experience stays identical across departments.

const fmt = (v) => (v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '0.00');

export default function PurchaseHistoryModal({ onCancel, onAdd }) {
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [step, setStep] = useState(1);
  const [activeDept, setActiveDept] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.getPurchaseHistoryDepartments();
        const data = res?.data || res;
        setDepartments(data?.departments || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openDepartment = async (dept) => {
    setActiveDept(dept);
    setStep(2);
    setItemsLoading(true);
    setSelectedIds([]);
    try {
      const res = await api.getPurchaseHistoryItems(dept.department_id);
      const data = res?.data || res;
      setItems(data?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setItemsLoading(false);
    }
  };

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filteredItems = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.description || '').toLowerCase().includes(q) ||
      (it.material_name || '').toLowerCase().includes(q) ||
      (it.vendor_name || '').toLowerCase().includes(q) ||
      (it.pr_number || '').toLowerCase().includes(q);
  });

  return (
    <Modal title="Select From Previous Purchases" onClose={onCancel} width={1100} headerBg="#0b318bff" headerColor="#ffffff">
      {step === 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Step 1 — choose a department to view its purchasing history</div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : departments.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No purchase history found yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14, maxHeight: '55vh', overflowY: 'auto' }}>
              {departments.map((d) => (
                <div
                  key={d.department_id}
                  onClick={() => openDepartment(d)}
                  style={{ border: '1px solid black', borderRadius: 10, padding: 16, cursor: 'pointer', background: '#f8fafc' }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#eff6ff'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                >
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0b318bff', marginBottom: 8 }}>{d.department_name}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569' }}>
                    <span>LINES: <strong>{d.line_count}</strong></span>
                    <span>VENDORS: <strong>{d.vendor_count}</strong></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>LAST DATE: {d.last_date || '—'}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#16a34a', marginTop: 6 }}>₦{fmt(d.total_value)}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn variant="outline" onClick={onCancel}>Close</Btn>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Step 2 — {activeDept?.department_name} · {items.length} purchase line(s)
            </div>
            <Btn variant="outline" size="sm" onClick={() => setStep(1)}>← Back to Departments</Btn>
          </div>
          <Input placeholder="Search description, material, vendor, or PR ref..." value={search} onChange={setSearch} />
          <div style={{ maxHeight: '45vh', overflowY: 'auto', border: '1px solid black', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#0b318bff', color: '#fff', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px 10px', width: 34 }}></th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>REQ. REF</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>DATE</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>MATERIAL</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>DESCRIPTION</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>VENDOR</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>PROJECT</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>GL</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center' }}>QTY</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>RATE</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {itemsLoading ? (
                  <tr><td colSpan={11} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
                ) : filteredItems.length === 0 ? (
                  <tr><td colSpan={11} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No matching purchase lines.</td></tr>
                ) : filteredItems.map((it) => {
                  const checked = selectedIds.includes(it.item_id);
                  return (
                    <tr key={it.item_id} onClick={() => toggle(it.item_id)} style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: checked ? '#e0e7ff' : '#fff' }}>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggle(it.item_id)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>{it.pr_number || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{it.pr_date || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{it.material_name || '—'}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{it.description}</td>
                      <td style={{ padding: '8px 10px' }}>{it.vendor_name || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{it.project_name || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{it.account_ledger_code || '—'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{fmt(it.quantity)} {it.unit_of_measure}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>₦{fmt(it.estimated_unit_rate)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>₦{fmt(it.estimated_amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{selectedIds.length} line(s) selected</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
              <Btn
                disabled={selectedIds.length === 0}
                onClick={() => onAdd(items.filter((it) => selectedIds.includes(it.item_id)))}
                style={{ background: '#0b318bff', color: '#fff', border: 'none' }}
              >
                Add Selected Line(s)
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
